import { useQuery, useQueries } from "@tanstack/react-query";
import { useWatch } from "react-hook-form";
import { useEffect, useState } from "react";
import { getCategoryRiders, getRiderProvisions, parseRiders } from "../../../utils/planUtils";
import FormSelect from "../../shared/FormSelect";
import { IoChevronForward, IoChevronDownOutline } from "react-icons/io5";
import ProvisionInfo from "./Provision";

const coverageTypes = ["DED", "DWV", "CPY", "CPY+DED"]

const initialFilterValue = {
  pcp: false,
  referral: false,
}

const initServiceCategory = (filter, serviceCategory) => {
  if (!(serviceCategory in filter)) {
    filter[serviceCategory] = {
      coverageTypes: new Set(),
      pcp: false,
      referral: false,
    }
  }
  return filter
}

const riderQuery = (templateKey, category) => {
  return useQuery({
    queryKey: ['riders', category],
    queryFn: async () => await getCategoryRiders(templateKey, category),
    staleTime: Infinity,
    enabled: !!templateKey,
  })
}

const provisionsQueries = (riders, ridersPending, collapsed) => {
  return useQueries({
    queries: (riders || []).map(r => ({
      queryKey: ['provisions', r.bnftPlanCompHistFactKey],
      queryFn: async () => await getRiderProvisions(r.bnftPlanCompHistFactKey),
      select: (provisions) => ({ ...r, provisions }),
      staleTime: Infinity,
      enabled: !!(!collapsed && !ridersPending),
    })),
    combine: (results) => ({
      data: results.map(r => r.data),
      isPending: results.some(r => r.isPending),
    }),
  })
}

const filterRiders = (parsedRiders, filters) => {
  return parsedRiders.parsedRiders.filter(r => {
    let match = true
    if (filters.pcp == true && r.riderInfo.pcp != true) match = false
    if (filters.referral == true && r.riderInfo.referral != true) match = false
    for (const serviceCategory of Object.keys(filters)) {
      const {
        coverageTypes,
        pcp,
        referral,
      } = filters[serviceCategory]
      const categoryInfo = r.riderInfo.serviceCategories[serviceCategory]

      if (coverageTypes?.size && !categoryInfo) match = false
      if (coverageTypes?.size && categoryInfo?.coverageTypes?.size && coverageTypes.intersection(categoryInfo.coverageTypes).size == 0) match = false
      if (pcp == true && categoryInfo?.pcp != true) match = false
      if (referral == true && categoryInfo?.referral != true) match = false
    }
    return match
  })
}

const CostSharing = ({ template, category, name, control, errors, title }) => {
  if (!category) return <div className="danger">Category is missing</div>

  const [collapsed, setCollapsed] = useState(true)
  const [filters, setFilters] = useState(initialFilterValue)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedRider, setSelectedRider] = useState(null)
  const [filteredRiders, setFilteredRiders] = useState([])
  const [parsedRiders, setParsedRiders] = useState([])

  const value = useWatch({ control, name })
  const templateKey = template?.additional?.benefitPlanTemplateKey

  const {
    data: riders,
    isPending: ridersPending,
  } = riderQuery(templateKey, category)

  const {
    data: ridersWithProvisions,
    isPending: ridersWithProvisionsPending,
  } = provisionsQueries(riders, ridersPending, collapsed)

  useEffect(() => {
    if (ridersWithProvisionsPending || ridersWithProvisions.length == 0) {
      setParsedRiders([])
      return
    }
    setParsedRiders(parseRiders(ridersWithProvisions))
  }, [ridersWithProvisions, ridersWithProvisionsPending])

  useEffect(() => {
    if (!value || parsedRiders.length == 0) {
      setSelectedRider(null)
      return
    }
    setSelectedRider(parsedRiders.parsedRiders.filter(r => r.benefitPlanCompName == value.value)[0])
  }, [value, parsedRiders])

  useEffect(() => {
    if (parsedRiders.length == 0) {
      setFilteredRiders([])
      return
    }
    setFilteredRiders(filterRiders(parsedRiders, filters))
  }, [filters, parsedRiders])

  const isPending = ridersPending || ridersWithProvisionsPending

  return (
    <div style={{ backgroundColor: 'var(--lightestGrey)', padding: '1rem 1rem 0rem 1rem', borderRadius: '.4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
        <div>
          <p className="cost-sharing-box-title" >
            {title}
            {!isPending && parsedRiders &&
              <span> ({filteredRiders.length})</span>}
          </p>
          <p className="sub-title" >{`Slot: ${category}`}</p>
        </div>

        {collapsed ?
          <IoChevronForward style={{ cursor: 'pointer' }} onClick={() => setCollapsed(!collapsed)} size={'1.2rem'} />
          :
          <IoChevronDownOutline style={{ cursor: 'pointer' }} onClick={() => setCollapsed(!collapsed)} size={'1.2rem'} />}
      </div>

      {collapsed ?
        <></>
        :
        <>
          {isPending ?
            <div className="loader" />
            :
            <div style={{ display: "flex", flexWrap: 'wrap', alignItems: "center", justifyContent: "space-between", padding: "1rem 1rem 1rem 0rem" }}>
              <div>
                <p className="clickable-link"
                  style={{ cursor: "pointer", color: "var(--green)" }}
                  onClick={() => setShowFilters(!showFilters)}>
                  {`${showFilters ? 'Hide' : 'Show'} Filters`}
                </p>
              </div>

              {showFilters &&
                <p style={{ cursor: "pointer", color: "var(--red)" }}
                  onClick={() => setFilters(initialFilterValue)}>
                  Clear Filters
                </p>}

              <div style={{ flexBasis: "100%", height: "0" }}></div>
              {showFilters &&
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', columnGap: '8rem', padding: '1rem 0rem 1rem 0rem' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', columnGap: '1rem', padding: '1rem 0rem 0rem 0rem' }}>
                      <input
                        type='checkbox'
                        name={`pcpRequired`}
                        style={{ cursor: 'pointer' }}
                        checked={filters.pcp}
                        onChange={() => setFilters({ ...filters, pcp: !filters.pcp })}
                        disabled={!parsedRiders.pcp}
                      />
                      <p style={{ width: '3rem' }}>PCP</p>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', columnGap: '1rem', padding: '1rem 0rem 0rem 0rem' }}>
                      <input
                        type='checkbox'
                        name={`referralRequired`}
                        style={{ cursor: 'pointer' }}
                        checked={filters.referral}
                        onChange={() => setFilters({ ...filters, referral: !filters.referral })}
                        disabled={!parsedRiders.referral}
                      />
                      <p style={{ width: '3rem' }}>Referral</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', columnGap: '8rem', padding: '1rem 0rem 1rem 0rem' }}>
                    {parsedRiders && parsedRiders.serviceCategories?.length && parsedRiders.serviceCategories.map(s => {
                      return (
                        <div key={`${s}`}>
                          <div style={{ flexBasis: "100%", height: "0" }}></div>
                          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', paddingBottom: '1rem' }}>
                            <p style={{ width: '10rem' }}>{s}</p>
                            <ul className='addons' style={{ padding: 0 }}>
                              {coverageTypes.map(coverageType => {
                                return (
                                  <li
                                    key={`${s}-${coverageType}`}
                                    style={{ cursor: 'pointer' }}
                                    className={`addon ${filters[s]?.coverageTypes?.has(coverageType) ? 'active' : ''}`}
                                    onClick={() => {
                                      let newFilters = structuredClone(filters)
                                      newFilters = initServiceCategory(newFilters, s)
                                      if (newFilters[s].coverageTypes.has(coverageType)) {
                                        newFilters[s].coverageTypes.delete(coverageType)
                                      } else {
                                        newFilters[s].coverageTypes.add(coverageType)
                                      }
                                      setFilters(newFilters)
                                    }}
                                  >
                                    {coverageType}
                                  </li>
                                )
                              })}
                            </ul>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </>}

              <FormSelect
                name={name}
                control={control}
                errors={errors}
                options={filteredRiders.map(r => ({
                  value: r.benefitPlanRiderHccId,
                  label: r.benefitPlanRiderHccId,
                  additional: r,
                }))} />
              {selectedRider &&
                <ProvisionInfo rider={selectedRider} />}
            </div>}
        </>}
    </div>
  )
}

export default CostSharing
